import React, {type JSX, useCallback, useEffect, useState} from "react";
import {useNotificacionStore} from "@rfacil/shared/hooks";

/**
 * @interface NotificationModalProps
 * @description Define las propiedades del componente de notificación modal.
 */
interface NotificationModalProps {
	notificacion: {
		id: number;
		mensaje: string;
		gravedad: "success" | "error" | "info" | "warning";
	};
	onClose: () => void;
}

/**
 * @component NotificationModal
 * @description Toast individual con diseño limpio tipo tarjeta: ícono circular por severidad,
 * título y mensaje, botón de cierre manual y una barra de progreso integrada al pie que marca
 * el tiempo restante antes del cierre automático.
 * @param {NotificationModalProps} props Las propiedades del componente.
 * @returns {JSX.Element | null} El toast renderizado o nulo.
 */
const NotificationModal: React.FC<NotificationModalProps> = ({
	notificacion,
	onClose,
}) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [progressWidth, setProgressWidth] = useState(100);

	const handleClose = useCallback(() => {
		setIsClosing(true);
		setTimeout(() => {
			onClose();
		}, 150);
	}, [onClose]);

	useEffect(() => {
		// Resetear estados cuando cambia la notificación
		setIsVisible(false);
		setIsClosing(false);
		setProgressWidth(100);

		// Animación de entrada
		const visibleTimer = setTimeout(() => setIsVisible(true), 10);

		// Animación de la barra de progreso
		const progressInterval = setInterval(() => {
			setProgressWidth((prev) => {
				if (prev <= 0) {
					clearInterval(progressInterval);
					return 0;
				}
				return prev - 2; // Reduce 2% cada 100ms (5 segundos total)
			});
		}, 100);

		// Cierre automático después de 5 segundos
		const autoCloseTimer = setTimeout(() => {
			handleClose();
		}, 5000);

		return () => {
			clearTimeout(autoCloseTimer);
			clearTimeout(visibleTimer);
			clearInterval(progressInterval);
		};
	}, [notificacion.id, handleClose]);

	// Configuración de colores e icono según la gravedad usando variables CSS personalizadas
	const getNotificationConfig = () => {
		const iconClassName = "w-4 h-4";
		switch (notificacion.gravedad) {
			case "success":
				return {
					accentColor: "var(--color-success-500)",
					iconColor: "var(--color-success-600)",
					iconBg: "var(--color-success-50)",
					titleColor: "var(--color-success-700)",
					progressColor: "var(--color-success-500)",
					icon: (
						<svg className={iconClassName} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
						</svg>
					),
					title: "Éxito",
				};
			case "error":
				return {
					accentColor: "var(--color-error-500)",
					iconColor: "var(--color-error-600)",
					iconBg: "var(--color-error-50)",
					titleColor: "var(--color-error-700)",
					progressColor: "var(--color-error-500)",
					icon: (
						<svg className={iconClassName} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
						</svg>
					),
					title: "Error",
				};
			case "warning":
				return {
					accentColor: "var(--color-warning-500)",
					iconColor: "var(--color-warning-600)",
					iconBg: "var(--color-warning-50)",
					titleColor: "var(--color-warning-700)",
					progressColor: "var(--color-warning-500)",
					icon: (
						<svg className={iconClassName} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={3}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
							/>
						</svg>
					),
					title: "Advertencia",
				};
			case "info":
			default:
				return {
					accentColor: "var(--color-info-500)",
					iconColor: "var(--color-info-600)",
					iconBg: "var(--color-info-50)",
					titleColor: "var(--color-info-700)",
					progressColor: "var(--color-info-500)",
					icon: (
						<svg className={iconClassName} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={3}
								d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					),
					title: "Información",
				};
		}
	};

	const config = getNotificationConfig();

	return (
		<div
			className={`fixed top-4 right-4 left-4 sm:left-auto w-auto sm:w-95 transition-all duration-300 ease-out ${
				isVisible && !isClosing
					? "opacity-100 translate-y-0"
					: "opacity-0 -translate-y-3"
			}`}
			// Por encima de MUI (~1300), overlays a pantalla completa del host (~10050) y diálogos (~12000)
			style={{zIndex: 13000}}
		>
			<div className="relative bg-background-primary rounded-xl shadow-xl border border-gray-200 overflow-hidden">
				<div className="flex items-start gap-3 p-4">
					{/* Ícono por severidad */}
					<div
						className="shrink-0 flex items-center justify-center rounded-full w-9 h-9"
						style={{backgroundColor: config.iconBg, color: config.iconColor}}
					>
						{config.icon}
					</div>

					{/* Mensaje */}
					<div className="flex-1 min-w-0 pt-0.5">
						<p className="text-sm font-semibold" style={{color: config.titleColor}}>
							{config.title}
						</p>
						<div
							className="text-sm text-gray-500 leading-snug mt-0.5"
							dangerouslySetInnerHTML={{__html: notificacion.mensaje}}
						/>
					</div>

					{/* Botón de cierre manual */}
					<button
						type="button"
						onClick={handleClose}
						aria-label="Cerrar notificación"
						className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
					>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Barra de progreso integrada al pie de la tarjeta */}
				<div className="h-1 w-full bg-gray-100">
					<div
						className="h-full transition-all duration-100 ease-linear"
						style={{
							width: isClosing ? "0%" : `${progressWidth}%`,
							backgroundColor: config.progressColor,
						}}
					/>
				</div>
			</div>
		</div>
	);
};

/**
 * Un componente global que muestra notificaciones tipo toast al usuario.
 * Se integra con un store de manejo de estado global (`useNotificacionStore`) para mostrar mensajes.
 * Las notificaciones aparecen como toasts compactos en la esquina superior derecha,
 * con cierre automático a los 5 segundos o manual mediante el botón de cierre.
 *
 * @component
 * @returns {JSX.Element} Componente que renderiza las notificaciones como toasts.
 */
const CustomSnackbar = (): JSX.Element => {
	const {notificaciones, closeNotification} = useNotificacionStore();

	// Mostrar solo la última notificación si hay múltiples
	const ultimaNotificacion =
		notificaciones.length > 0 ? notificaciones[notificaciones.length - 1] : null;

	if (!ultimaNotificacion) return <></>;

	return (
		<NotificationModal
			key={ultimaNotificacion.id}
			notificacion={ultimaNotificacion}
			onClose={() => closeNotification(ultimaNotificacion.id)}
		/>
	);
};

export default CustomSnackbar;
